import get from 'lodash/get';
import P from 'prop-types';
import { useMemo } from 'react';
import { useMediaQuery } from '~/app/lib/use-media-query';
import {
  boolCheckboxSpec,
  group,
  numberInputSpec,
  stringInputSpec,
  stringSelectSpec,
  stringTextAreaSpec,
  wrapper,
} from '../../lib/editing-configs';
import UniversalContainer, {
  universalContainerDefaultProps,
  universalContainerPropTypes,
} from '../universal-container';
import BaseLottieAnimation from './base-lottie-animation';
import { animationModes } from './lib';

const lottiePlayerId = 'lottie-player';

const LottieAnimation = ({
  container,
  animationData,
  animationMode,
  width,
  height,
  desktopWidth,
  desktopHeight,
  tabletWidth,
  tabletHeight,
  mobileWidth,
  mobileHeight,
  visibility,
  frames,
  dimensionsPerViewport,
}) => {
  const size = useMediaQuery();

  // Every time "dimensionsPerViewport' changes we'll re-map dimensions to match the viewport.
  // If viewport is not provided we'll used the last one
  const dimensions = useMemo(
    () =>
      dimensionsPerViewport
        ? {
            lg: { width: desktopWidth, height: desktopHeight },
            md: {
              width: tabletWidth || desktopWidth,
              height: tabletHeight || desktopHeight,
            },
            sm: {
              width: mobileWidth || tabletWidth,
              height: mobileHeight || tabletHeight,
            },
          }[size]
        : { width, height },
    [
      desktopHeight,
      desktopWidth,
      dimensionsPerViewport,
      height,
      mobileHeight,
      mobileWidth,
      size,
      tabletHeight,
      tabletWidth,
      width,
    ]
  );

  return (
    <UniversalContainer {...container}>
      <BaseLottieAnimation
        lottiePlayerId={lottiePlayerId}
        animationData={animationData}
        animationMode={animationMode}
        visibility={visibility}
        frames={frames}
        dimensions={dimensions}
      />
    </UniversalContainer>
  );
};

LottieAnimation.displayName = 'LottieAnimation';

LottieAnimation.propTypes = {
  container: universalContainerPropTypes,
  animationData: P.string,
  animationMode: P.string,
  width: P.number,
  height: P.number,
  desktopWidth: P.number,
  desktopHeight: P.number,
  tabletWidth: P.number,
  tabletHeight: P.number,
  mobileWidth: P.number,
  mobileHeight: P.number,
  dimensionsPerViewport: P.bool,
  visibility: P.shape({
    x: P.string,
    y: P.string,
  }),
  frames: P.shape({
    x: P.string,
    y: P.string,
  }),
};

LottieAnimation.defaultProps = {
  container: universalContainerDefaultProps,
  animationData: '',
  animationMode: animationModes.SEEK,
  width: 300,
  height: 300,
  visibility: {
    x: '0.5',
    y: '1.0',
  },
  frames: {
    x: '0.5',
    y: '1.0',
  },
};

const isTestEnv = process?.env?.NODE_ENV === 'test';

const optionsObj = {
  disableGroup: false,
};
// in order to make the test work properly we set "disableGroup" true when in test env
optionsObj.disableGroup = !isTestEnv;

LottieAnimation.getEditingConfig = (
  componentDocument,
  fieldLabel,
  fieldName,
  options = {
    ...optionsObj,
  }
) => {
  // because we need to know when the component is used standalone or in a <Grid>
  // when used in standalone the props will be in componentDocument.props.myProps
  // when used in <Grid> the props will be in componentDocument.props.nested.field.name.myProps
  const getInputName = (name) => {
    if (fieldName) {
      return `${fieldName}.${name}`;
    }
    return name;
  };

  const initialAnimationMode = get(
    componentDocument,
    `props.${getInputName('animationMode')}`, // When used inside of Grid getInputName will handle props path
    animationModes.SEEK
  );

  const visibilityInputFields = ['x', 'y'].map((axis) =>
    stringInputSpec(
      getInputName(`visibility.${axis}`),
      `Visibility ${axis.toUpperCase()}`
    )
  );

  const framesInputFields = ['x', 'y'].map((axis) =>
    stringInputSpec(
      getInputName(`frames.${axis}`),
      `Frames ${axis.toUpperCase()}`
    )
  );

  const whenVisibleInputConfig =
    initialAnimationMode === animationModes.PLAY
      ? [...visibilityInputFields]
      : [];

  const onScrollInputConfig =
    initialAnimationMode === animationModes.SEEK
      ? [...visibilityInputFields, ...framesInputFields]
      : [];

  const outputDimensionsPerViewPort = get(
    componentDocument,
    `props.${getInputName('dimensionsPerViewport')}`,
    false
  );

  const dimensionsInputConfig = outputDimensionsPerViewPort
    ? [
        numberInputSpec(getInputName('desktopWidth'), 'Desktop Width'),
        numberInputSpec(getInputName('desktopHeight'), 'Desktop Height'),
        numberInputSpec(getInputName('tabletWidth'), 'Tablet Width'),
        numberInputSpec(getInputName('tabletHeight'), 'Tablet Height'),
        numberInputSpec(getInputName('mobileWidth'), 'Mobile Width'),
        numberInputSpec(getInputName('mobileHeight'), 'Mobile Height'),
      ]
    : [
        numberInputSpec(getInputName('width'), 'Width'),
        numberInputSpec(getInputName('height'), 'Height'),
      ];

  const editorPanelOptions = [
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Container',
      [
        'backgroundColor',
        'backgroundImage',
        'margin',
        'padding',
        'hide',
        'jumpLinkId',
      ],
      getInputName('container')
    ),
    stringTextAreaSpec(getInputName('animationData'), 'Animation Data'),
    stringSelectSpec(getInputName('animationMode'), 'Animation Mode', [
      { label: 'On Scroll', value: animationModes.SEEK },
      { label: 'When Visible', value: animationModes.PLAY },
    ]),
    boolCheckboxSpec(
      getInputName('dimensionsPerViewport'),
      'Unique Dimensions Per Viewport?'
    ),
    ...dimensionsInputConfig,
    ...whenVisibleInputConfig,
    ...onScrollInputConfig,
  ];

  if (options.disableGroup) {
    return [wrapper([...editorPanelOptions])];
  }
  return [group(fieldLabel, [...editorPanelOptions])];
};

export default LottieAnimation;
